import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Building2,
  DollarSign,
  Save,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3,
  Phone,
  MapPin,
  Briefcase,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/RecruiterAddEmployee.css";

const RecruiterAddEmployee = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    employee_name: "",
    employee_number: "",
    customer_id: "",
    type: "קבלן",
    payment_method: "קבלן",
    hour_rate: "",
    weekend_hour_rate: "",
    michpal_id: "",
    phone_number: "",
    address: "",
  });

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  // Common Israel cities and areas for suggestions
  const israelCities = [
    "תל אביב",
    "ירושלים",
    "חיפה",
    "רישון לציון",
    "פתח תקווה",
    "אשדוד",
    "נתניה",
    "באר שבע",
    "בני ברק",
    "חולון",
    "בת ים",
    "רמת גן",
    "אשקלון",
    "רחובות",
    "הרצליה",
    "כפר סבא",
    "הוד השרון",
    "מודיעין",
    "נהריה",
    "רעננה",
    "גבעתיים",
    "ראש העין",
    "ביתר עלית",
    "אילת",
    "אשקלון",
    "לוד",
    "נצרת",
    "עפולה",
    "קרית",
    "טבריה",
    "עכו",
    "צפת",
    "יבנה",
    "נשר הנשיא",
    "אום אל פחם",
    "רמלה",
    "קרית אתא",
    "קרית גת",
    "קרית מלאכי",
    "קרית ים",
    "בית שמש",
    "גבעת שמואל",
    "פתח תקווה",
    "ראשון לציון",
    "מעלות אדומים",
  ];

  // Get unique customer names
  const uniqueCustomerNames = [...new Set(customers.map((c) => c.name))]
    .filter(Boolean)
    .sort();

  // Get sites for selected customer
  const sitesForCustomer = customers.filter(
    (c) => c.name === selectedCustomerName
  );

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers({ limit: 1000 });
      setCustomers(response.customers || []);
    } catch (error) {
      console.error("Failed to load customers:", error);
      setError("שגיאה בטעינת לקוחות");
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.getMyRecruiterStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Phone number - digits only
    if (name === "phone_number") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
      return;
    }

    // Address - show suggestions
    if (name === "address") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (value.length > 0) {
        const filtered = israelCities
          .filter((city) => city.includes(value))
          .slice(0, 8);
        setAddressSuggestions(filtered);
        setShowAddressSuggestions(filtered.length > 0);
      } else {
        setShowAddressSuggestions(false);
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
    setSuccess(false);
  };

  const selectAddress = (address) => {
    setFormData((prev) => ({
      ...prev,
      address: address,
    }));
    setShowAddressSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      // Validate hour_rate (max 45 for recruiters)
      if (formData.hour_rate && parseFloat(formData.hour_rate) > 45) {
        setError('תעריף שעתי מקסימלי הוא 45 ש"ח');
        setLoading(false);
        return;
      }

      const response = await apiService.createNewEmployee(formData);

      if (response.success) {
        setSuccess(true);
        loadStats(); // Refresh stats after successful submission
        // Reset form
        setFormData({
          employee_name: "",
          employee_number: "",
          customer_id: "",
          type: "קבלן",
          payment_method: "קבלן",
          hour_rate: "",
          weekend_hour_rate: "",
          michpal_id: "",
          phone_number: "",
          address: "",
        });
        setSelectedCustomerName("");
      } else {
        setError(response.error || "שגיאה בשליחת העובד");
      }
    } catch (error) {
      setError(error.response?.data?.error || "שגיאה בשליחת העובד");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='recruiter-page'>
      <div className='recruiter-header'>
        <div className='header-icon'>
          <UserPlus size={32} />
        </div>
        <div className='header-text'>
          <h1>הוספת עובד חדש</h1>
          <p>מלא את פרטי העובד ושלח לאישור המנהל</p>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className='stats-section'>
          <div className='stats-header'>
            <BarChart3 size={20} />
            <span>הסטטיסטיקות שלי</span>
          </div>
          <div className='stats-cards'>
            <div className='stat-card pending'>
              <Clock size={24} />
              <div className='stat-value'>{stats.pending}</div>
              <div className='stat-label'>ממתינים לאישור</div>
            </div>
            <div className='stat-card approved'>
              <CheckCircle size={24} />
              <div className='stat-value'>{stats.approved}</div>
              <div className='stat-label'>אושרו</div>
            </div>
            <div className='stat-card rejected'>
              <XCircle size={24} />
              <div className='stat-value'>{stats.rejected}</div>
              <div className='stat-label'>נדחו</div>
            </div>
            <div className='stat-card total'>
              <UserPlus size={24} />
              <div className='stat-value'>{stats.total_submitted}</div>
              <div className='stat-label'>סה"כ הוגשו</div>
            </div>
          </div>
          {stats.monthly && stats.monthly.length > 0 && (
            <div className='monthly-stats'>
              <h4>לפי חודש</h4>
              <table className='monthly-table'>
                <thead>
                  <tr>
                    <th>חודש</th>
                    <th>ממתינים</th>
                    <th>אושרו</th>
                    <th>נדחו</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.monthly.map((m) => (
                    <tr key={m.month}>
                      <td>{m.month}</td>
                      <td className='pending-cell'>{m.pending || 0}</td>
                      <td className='approved-cell'>{m.approved || 0}</td>
                      <td className='rejected-cell'>{m.rejected || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className='recruiter-form-container'>
        {success && (
          <div className='success-message'>
            <CheckCircle size={20} />
            <span>העובד נשלח לאישור בהצלחה!</span>
          </div>
        )}

        {error && (
          <div className='error-message'>
            <span>❌ {error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className='recruiter-form'>
          <div className='form-section'>
            <h3>פרטי עובד</h3>

            <div className='form-row'>
              <div className='form-field'>
                <label>
                  שם עובד <span className='required'>*</span>
                </label>
                <input
                  type='text'
                  name='employee_name'
                  value={formData.employee_name}
                  onChange={handleChange}
                  required
                  placeholder='הזן שם עובד'
                />
              </div>

              <div className='form-field'>
                <label>מספר עובד</label>
                <input
                  type='text'
                  name='employee_number'
                  value={formData.employee_number}
                  onChange={handleChange}
                  placeholder='הזן מספר עובד'
                />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field'>
                <label>
                  <Phone size={14} style={{ marginLeft: "4px" }} />
                  טלפון
                </label>
                <input
                  type='tel'
                  name='phone_number'
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder='הזן מספר טלפון'
                />
              </div>

              <div className='form-field'>
                <label>
                  <MapPin size={14} style={{ marginLeft: "4px" }} />
                  כתובת
                </label>
                <input
                  type='text'
                  name='address'
                  value={formData.address}
                  onChange={handleChange}
                  onBlur={() =>
                    setTimeout(() => setShowAddressSuggestions(false), 200)
                  }
                  onFocus={() => {
                    if (formData.address && addressSuggestions.length > 0) {
                      setShowAddressSuggestions(true);
                    }
                  }}
                  placeholder='הזן עיר או כתובת'
                  autoComplete='off'
                />
                {showAddressSuggestions && (
                  <div className='address-suggestions'>
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className='address-suggestion-item'
                        onClick={() => selectAddress(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='form-section'>
            <h3>
              <Briefcase size={18} />
              סוג עובד ותשלום
            </h3>

            <div className='form-row'>
              <div className='form-field'>
                <label>סוג עובד</label>
                <select
                  name='type'
                  value={formData.type}
                  onChange={handleChange}
                >
                  <option value='קבלן'>קבלן</option>
                  <option value='חברה'>חברה</option>
                  <option value='קבלן ו חברה'>קבלן ו חברה</option>
                </select>
              </div>

              <div className='form-field'>
                <label>אופן תשלום</label>
                <select
                  name='payment_method'
                  value={formData.payment_method}
                  onChange={handleChange}
                >
                  <option value='קבלן'>קבלן</option>
                  <option value='העברה בנקאית'>העברה בנקאית</option>
                  <option value="צ'ק">צ'ק</option>
                </select>
              </div>
            </div>
          </div>

          <div className='form-section'>
            <h3>
              <Building2 size={18} />
              לקוח ואתר
            </h3>

            <div className='form-row'>
              <div className='form-field'>
                <label>
                  לקוח <span className='required'>*</span>
                </label>
                <select
                  value={selectedCustomerName}
                  onChange={(e) => {
                    setSelectedCustomerName(e.target.value);
                    setFormData((prev) => ({ ...prev, customer_id: "" }));
                  }}
                  required
                >
                  <option value=''>בחר לקוח...</option>
                  {uniqueCustomerNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='form-field'>
                <label>
                  אתר <span className='required'>*</span>
                </label>
                <select
                  name='customer_id'
                  value={formData.customer_id}
                  onChange={handleChange}
                  disabled={!selectedCustomerName}
                  required
                >
                  <option value=''>בחר אתר...</option>
                  {sitesForCustomer.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.site_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className='form-section'>
            <h3>
              <DollarSign size={18} />
              תעריפים
            </h3>

            <div className='form-row'>
              <div className='form-field'>
                <label>תעריף שעתי (₪)</label>
                <input
                  type='number'
                  name='hour_rate'
                  value={formData.hour_rate}
                  onChange={handleChange}
                  step='0.01'
                  min='0'
                  max='45'
                  placeholder='מקסימום 45'
                />
                <span className='field-hint'>מקסימום 45 ש"ח</span>
              </div>

              <div className='form-field'>
                <label>תעריף שעתי סופ"ש (₪)</label>
                <input
                  type='number'
                  name='weekend_hour_rate'
                  value={formData.weekend_hour_rate}
                  onChange={handleChange}
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field'>
                <label>מזהה מיכפל</label>
                <input
                  type='number'
                  name='michpal_id'
                  value={formData.michpal_id}
                  onChange={handleChange}
                  placeholder='הזן מזהה מיכפל'
                />
              </div>
            </div>
          </div>

          <div className='form-actions'>
            <button type='submit' className='btn-submit' disabled={loading}>
              {loading ? (
                "שולח..."
              ) : (
                <>
                  <Save size={18} />
                  שלח לאישור
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecruiterAddEmployee;
